import _ from "lodash";

const SEGMENT_REGEX = /(\[[^\]]+\])|([a-zA-Z_$][\w$]*)/g;

const PATH_TYPE = {
  PROPERTY: "property",
  ARRAY_INDEX: "array_index",
  ARRAY_OBJECT: "array_object",
};

export function getPathType(segment) {
  if (/^\[\d+\]$/.test(segment)) {
    return {
      type: PATH_TYPE.ARRAY_INDEX,
      index: Number(segment.slice(1, -1)),
    };
  } else if (/^\[[^:]+:.+\]$/.test(segment)) {
    const [key, value] = segment.slice(1, -1).split(":");
    return {
      type: PATH_TYPE.ARRAY_OBJECT,
      key,
      value,
    };
  } else {
    return {
      type: PATH_TYPE.PROPERTY,
      property: segment,
    };
  }
}

export function getValueByPath(obj, pathArray) {
  let result = obj;
  let objectIndex = -1;
  _.forEach(pathArray, (path, index) => {
    const pathParam = getPathType(path);
    if (pathParam.type === PATH_TYPE.ARRAY_INDEX) {
      result = result[pathParam.index];
    } else if (pathParam.type === PATH_TYPE.ARRAY_OBJECT) {
      const { key, value } = pathParam;
      if (index === pathArray.length - 1) {
        objectIndex = result.findIndex(
          (item) => item[key].toString() === value.toString()
        );
      }
      result = result.find((item) => item[key].toString() === value.toString());
    } else {
      result = _.get(result, pathParam.property);
    }
  });
  return {
    value: result,
    index: objectIndex,
  };
}

function processRollback({ pathArray, from, to, original, data }) {
  // X -> O
  const isRemove = _.isNil(from) && !_.isNil(to);

  // O -> X
  const isAdd = _.isNil(to) && !_.isNil(from);

  _.forEach(pathArray, (path, idx) => {
    const isLast = idx === pathArray.length - 1;
    const pathParam = getPathType(path);

    if (pathParam.type === PATH_TYPE.ARRAY_INDEX) {
      // [123]
      const { index } = pathParam;
      if (!Array.isArray(data) || (index >= data.length && !isAdd)) {
        throw new Error(`Invalid array index access at ${path}`);
      }

      if (isLast) {
        if (isRemove) {
          data.splice(index, 1);
        } else if (isAdd) {
          data.splice(index, 0, from);
        } else {
          data[index] = from;
        }
      } else {
        data = data[index];
      }
    } else if (pathParam.type === PATH_TYPE.ARRAY_OBJECT) {
      // [key:value]
      const { key, value } = pathParam;
      if (!Array.isArray(data)) {
        throw new Error(`Invalid object array access at ${path}`);
      }
      let foundIndex = data.findIndex(
        (item) => item[key].toString() === value.toString()
      );

      if (foundIndex === -1 && !isAdd) {
        throw new Error(`No matching item found for ${path}`);
      }

      if (isLast) {
        if (isRemove) {
          data.splice(foundIndex, 1);
        } else if (isAdd) {
          const existing = getValueByPath(original, pathArray.slice(0, -1));
          const existingList = existing.value.map((d) => d[key]);
          data.push(from);
          data.sort((a, b) => {
            const aIndex = existingList.indexOf(a[key]);
            const bIndex = existingList.indexOf(b[key]);
            return aIndex - bIndex;
          });
          foundIndex = data.findIndex(
            (item) => item[key].toString() === value.toString()
          );
        } else {
          data[foundIndex] = from;
        }
      } else {
        data = data[foundIndex];
      }
    } else {
      // property
      const { property } = pathParam;
      if (_.isNil(data) || _.isObject(data) === false) {
        throw new Error(`Invalid property access at ${property}`);
      }

      if (isLast) {
        if (isRemove) {
          delete data[property];
        } else if (isAdd) {
          data[property] = from;
        } else {
          data[property] = from;
        }
      } else {
        data = data[property];
      }
    }
  });
}

export function setValueByPath(params) {
  const { original, changed, path } = params;
  const pathArray = path.match(SEGMENT_REGEX) || [];
  const newObj = _.cloneDeep(changed);
  let result = newObj;

  const fromData = getValueByPath(original, pathArray).value;
  const toData = getValueByPath(changed, pathArray).value;

  processRollback({
    pathArray,
    from: fromData,
    to: toData,
    original,
    data: result,
  });

  return newObj;
}

export function setFirstLookValueByPath(params) {
  const { original, changed, path } = params;
  const pathArray = path.match(SEGMENT_REGEX) || [];
  const newObj = _.cloneDeep(changed);
  let result = newObj;

  const from = _.cloneDeep(params.from);
  const to = _.cloneDeep(params.to);

  const lastPath = pathArray[pathArray.length - 1];
  const onlyPriorityChange = lastPath === "priority";
  const priFrom = onlyPriorityChange ? from : _.get(from, "priority");
  const priTo = onlyPriorityChange ? to : _.get(to, "priority");

  if (onlyPriorityChange) {
    pathArray.pop();
  }

  const fromData = getValueByPath(original, pathArray).value;
  const toData = getValueByPath(changed, pathArray).value;

  if (!onlyPriorityChange) {
    processRollback({
      pathArray,
      from: fromData,
      to: toData,
      original,
      data: result,
    });
  }

  const newItem = getValueByPath(result, pathArray);

  if (!_.isNil(priFrom) && !_.isNil(priTo)) {
    if (priFrom === "--") {
      result[newItem.index].enabled = false;
    } else if (priTo === "--") {
      const { value } = getValueByPath(original, [...pathArray, "enabled"]);
      result[newItem.index].enabled = value;
    } else {
      const originalValue = getValueByPath(original, pathArray);
      const changedValue = getValueByPath(changed, pathArray);

      if (originalValue.index !== -1 && changedValue.index !== -1) {
        // Remove the item from its current position
        newObj.splice(changedValue.index, 1);
        // Insert the item at the original position
        newObj.splice(originalValue.index, 0, newItem.value);
      }
    }
  }

  return newObj;
}
